import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/infrastructure/supabase/client'
import { useAuthStore } from '@/store/authStore'

export interface AppNotification {
  id: string
  tenant_id: string
  type:
    | 'payment_success'
    | 'payment_failed'
    | 'subscription_canceled'
    | 'subscription_updated'
    | 'site_published'
    | 'site_unpublished'
    | 'page_published'
    | 'page_unpublished'
  title: string
  body: string | null
  read: boolean
  metadata: Record<string, unknown>
  created_at: string
}

export function useNotifications() {
  const { tenant } = useAuthStore()
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [loading, setLoading] = useState(true)
  // Each mounted instance needs a unique channel name; Supabase throws if two
  // components try to add postgres_changes listeners to the same channel name
  // after it has already been subscribed (e.g. NotificationBell + NotificationsPage).
  const instanceId = useRef(`${Math.random().toString(36).slice(2)}`)

  useEffect(() => {
    if (!tenant?.id) return

    supabase
      .from('notifications')
      .select('*')
      .eq('tenant_id', tenant.id)
      .order('created_at', { ascending: false })
      .limit(50)
      .then(({ data }) => {
        setNotifications((data as AppNotification[]) ?? [])
        setLoading(false)
      })

    const channel = supabase
      .channel(`notifications:${tenant.id}:${instanceId.current}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `tenant_id=eq.${tenant.id}`,
        },
        (payload) => {
          const notif = payload.new as AppNotification
          setNotifications((prev) => [notif, ...prev])
          toast(notif.title, { description: notif.body ?? undefined })
        },
      )
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [tenant?.id])

  const markRead = async (id: string) => {
    await supabase.from('notifications').update({ read: true }).eq('id', id)
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)))
  }

  const markAllRead = async () => {
    if (!tenant?.id) return
    await supabase.from('notifications').update({ read: true }).eq('tenant_id', tenant.id).eq('read', false)
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }

  const unreadCount = notifications.filter((n) => !n.read).length

  return { notifications, loading, unreadCount, markRead, markAllRead }
}
